import CategoryBanner, { CategoryBannerProps } from 'components/CategoryBanner/CategoryBanner';
import ProductCardwVTO from 'components/ProductCard/ProductCardwVTO';
import { SeoMetaTags, SeoBlock, CategoryLensTypes, FrameShape } from 'constants/dataTypes';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { slugToString } from 'functions/slug';
import { titleCase } from 'functions/capitalize';
import CategorySeoText from 'components/Others/CategorySeoText';
import CategoryShapeSwitcher from 'components/CategoryShapeSwitcher/CategoryShapeSwitcher';
import ColorFilter, { colorMap } from 'components/ColorFilter/ColorFilter';
import useWindowSize from 'hooks/useWindowSize';
import { addDataLayer } from 'lib/ga';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import { getCategoryTitle } from 'functions/categoryTitle';
import { CategoryData } from 'types/category';
import CircleArrow from 'components/Icons/CircleArrow';

interface EcommerceCategory {
  slug: string;
  name: string;
  regular_price: string;
  discounted_price: string;
}

interface GlassSelection {
  shape: string;
  arm_color: string;
  lens_color: string;
  treatment: string;
  frame_color: string;
  ecommerce_category: EcommerceCategory;
}


const DynamicVTOModalV2 = dynamic(() => import('components/VTO/VTOModalV2'), {
  ssr: false,
  loading: () => null
});

function formatColorName(colorSlug: string): string {
  return titleCase(slugToString(colorSlug));
}

export interface EyewearSelectionPageProps {
  seoMetaTags: SeoMetaTags;
  categoryBannerProps: CategoryBannerProps;
  params: any;
  seoBlock: SeoBlock;
  pageData?: CategoryData;
}

export default function EyewearSelectionPage({
  seoMetaTags,
  categoryBannerProps,
  params,
  seoBlock,
  pageData
}: EyewearSelectionPageProps) {
  const { width } = useWindowSize();
  const router = useRouter();
  const [selectedSlugs, setSelectedSlugs] = useState<Array<string>>([]);
  const [showVTOModal, setShowVTOModal] = useState(false);

  const { shape, colour } = router.query;

  const colorTitle = useMemo(() => {
    if (!colour) return null;
    return getCategoryTitle({
      query: {
        colour,
        shape: shape as string
      }
    });
  }, [colour, shape]);

  const data = useMemo(() => {
    const items = [
      {
        id: 1,
        href: '',
        text: 'Dresden Vision'
      },
      {
        id: 2,
        href: 'eyewear-selection',
        text: 'Eyewear Selection'
      }
    ];

    // Add base category
    items.push({
      id: 3,
      href: `eyewear-selection/${params.category}`,
      text: categoryBannerProps.title
    });

    // If shape is selected, add shape to breadcrumb
    if (shape) {
      const shapeCapitalized = titleCase(shape as string);
      items.push({
        id: 4,
        href: `eyewear-selection/${params.category}/shape/${shape}`,
        text: `${shapeCapitalized} ${categoryBannerProps.title}`
      });

      // Only add color to breadcrumb if exactly one color is selected
      if (colour && !Array.isArray(colour) && colorTitle) {
        const cleanColorTitle = colorTitle.replace('Original', '').replace('Capote', '').trim();
        items.push({
          id: 5,
          href: `eyewear-selection/${params.category}/shape/${shape}/colour/${colour}`,
          text: `${shapeCapitalized} ${categoryBannerProps.title} - ${cleanColorTitle} Frames`
        });
      }
    }
    // If only color is selected (no shape)
    else if (colour && !Array.isArray(colour) && colorTitle) {
      const cleanColorTitle = colorTitle.replace('Original', '').replace('Capote', '').trim();
      items.push({
        id: 4,
        href: `eyewear-selection/${params.category}/colour/${colour}`,
        text: `${categoryBannerProps.title} - ${cleanColorTitle} Frames`
      });
    }

    return items;
  }, [categoryBannerProps.title, params.category, shape, colour, colorTitle]);

  // Update the banner title logic
  const bannerTitle = useMemo(() => {
    let title = categoryBannerProps.title;

    // If shape is selected, add shape to breadcrumb
    if (shape) {
      const shapeCapitalized = titleCase(shape as string);
      title = `${shapeCapitalized} ${categoryBannerProps.title}`;
      // Only add color to title if exactly one color is selected
      if (colour && !Array.isArray(colour) && colorTitle) {
        const cleanColorTitle = colorTitle.replace('Original', '').replace('Capote', '').trim();
        title = `${shapeCapitalized} ${categoryBannerProps.title} - ${cleanColorTitle} Frames`;
      }
    } else if (colour && !Array.isArray(colour) && colorTitle) {
      // If only color is selected (no shape)
      const cleanColorTitle = colorTitle.replace('Original', '').replace('Capote', '').trim();
      title = `${categoryBannerProps.title} - ${cleanColorTitle} Frames`;
    }

    return title;
  }, [categoryBannerProps.title, shape, colour, colorTitle]);

  const frameShapes = useMemo(() => {
    const selections = pageData?.glass_selections;
    if (!selections || selections.length === 0) {
      return ['classic', 'round'];
    }

    let shapes = Array.from(new Set(selections.map(frame => frame.shape)));
    shapes.sort((a, b) => {
      if (a === 'classic') return -1;
      if (b === 'classic') return 1;
      return a.localeCompare(b);
    });
    return shapes;
  }, [pageData?.glass_selections]);

  const frameList = useMemo(() => {
    const selections = pageData?.glass_selections;
    if (!selections || selections.length === 0) return [];
      console.log('selections', selections);
    return selections.filter((frame) => {
      const { shape, colour } = router.query;
      const normalizedQueryShape = shape === 'rectangle' ? 'classic' : shape;

      if (shape && frame.shape !== normalizedQueryShape) {
        return false;
      }

      // Handle color filtering
      if (colour) {
        if (Array.isArray(colour)) {
          const mappedColors = colour.map(c => colorMap.get(c) || [c]).flat();
          if (!mappedColors.includes(frame.frame_color)) {
            return false;
          }
        } else {
          const mappedColors = colorMap.get(colour) || [colour];
          if (!mappedColors.includes(frame.frame_color)) {
            return false;
          }
        }
      }

      if (selectedSlugs.length > 0 && !selectedSlugs.includes(frame.frame_color)) {
        return false;
      }
      return true;
    });
  }, [pageData?.glass_selections, router.query, selectedSlugs]);

  const categoryFrameList = useMemo(() => {
    const selections = pageData?.glass_selections;
    if (!selections || selections.length === 0) return [];

    return selections.map(frame => ({
      frame_color: {
        name: formatColorName(frame.frame_color),
        slug: frame.frame_color
      },
      arms_color: {
        name: formatColorName(frame.arm_color),
        slug: frame.arm_color
      },
      frame_shape: frame.shape
    }));
  }, [pageData?.glass_selections]);


  return (
    <div>
      <Head>
        <title>{bannerTitle + ' | Eyewear Selections | Dresden Vision'}</title>
        <meta
          name="description"
          content={pageData?.metaDescription || ''}></meta>
        <meta property="og:image:width" content="1242" />
        <meta
          property="og:description"
          content={pageData?.metaDescription || ''}
        />
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="719" />
        <meta name="twitter:title" content={`${bannerTitle} | Dresden Vision`} />
        <meta name="og:title" content={`${bannerTitle} | Dresden Vision`} />
      </Head>
      <CategoryBanner {...categoryBannerProps} title={bannerTitle} breadcrumbItems={data}>
        {/* <div className="flex flex-col md:flex-row md:justify-end items-center space-y-4 md:space-y-0 md:space-x-16 mt-4">
          <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4 w-full md:w-auto">
            <CategoryShapeSwitcher frameShapes={frameShapes} />
          </div>
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-4">
            <ColorFilter
              router={router}
              setSelectedSlugs={setSelectedSlugs}
              categoryFrameList={categoryFrameList}
            />
          </div>
        </div> */}
      </CategoryBanner>
      <div className="mx-auto max-w-[1224px] px-4 pt-2">
        <div className="mt-[24px] grid grid-cols-1 gap-y-[20px] md:grid-cols-2 md:gap-x-[15px] md:gap-y-[30px] shop:grid-cols-3">
          {frameList.map((frame, index) => (
            <ProductCardwVTO
              key={`${frame.shape}-${frame.frame_color}-${index}`}
              onClickProduct={() => {
                addDataLayer({
                  event: 'productClick',
                  ecommerce: {
                    click: {
                      actionField: { list: 'category' },
                      products: [
                        {
                          name: `${pageData?.title || ''} - ${formatColorName(frame.frame_color)}`,
                          category: pageData?.title || '',
                          variant: formatColorName(frame.frame_color),
                          list: 'Category',
                          position: index
                        }
                      ]
                    }
                  }
                });
              }}
              width={width}
              categoryLensTypes={frame.ecommerce_category.categoryLensTypes || []}
              initialLensColor={frame.lens_color.replace('_', '-')}
              data={{
                title: formatColorName(frame.frame_color),
                categorySlug: frame.ecommerce_category.slug,
                colorSlug: frame.frame_color,
                armColorSlug: frame.arm_color,
                price: Number(frame.ecommerce_category.price),
                regularPrice: Number(frame.ecommerce_category.regular_price),
                href: `/shop/${frame.ecommerce_category.slug}/${frame.frame_color}`,
                shape: frame.shape as FrameShape,
                isPriority: width > 600 ? index <= 1 : index == 0
              }}
              onVTOToggle={() => setShowVTOModal(true)}
            />
          ))}
        </div>
        {pageData?.page_bottom_link_text && pageData?.page_bottom_link_url && (
          <div className="mt-16 mb-8 flex justify-center">
            <a 
              href={(router.locale !== 'international' ? '/' + router.locale : '') + pageData.page_bottom_link_url} 
              className="inline-flex items-center gap-3 justify-center px-24 py-6 text-sm font-medium transition-all duration-200 hover:opacity-90 group"
              style={{
                backgroundColor: pageData.page_bottom_link_bg_color || '#FFF5F1',
                color: pageData.page_bottom_link_text_color || '#FF6B2C'
              }}
            >
              <span>{pageData.page_bottom_link_text}</span>
              <CircleArrow 
                className="w-7 h-7 transition-transform duration-300 group-hover:translate-x-1" 
                circleFill={pageData.page_bottom_link_bg_color || '#FFF5F1'}
                arrowFill={pageData.page_bottom_link_text_color || '#FF6B2C'}
                strokeColor={pageData.page_bottom_link_text_color || '#FF6B2C'}
                strokeWidth={2}
              />
            </a>
          </div>
        )}
        <CategorySeoText seoBlock={seoBlock} />
      </div>
      <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
      {showVTOModal && (
        <DynamicVTOModalV2 
          title={categoryBannerProps.title}
          categoryLensTypes={frameList[0]?.ecommerce_category.categoryLensTypes || []}
        />
      )}
    </div>
  );
} 